/*
 * V3 analytic water surface over a photographic coast. Browser-review candidate.
 * No network requests or external dependencies.
 * Inputs: .coast-scene, img[data-coast-image], optional img[data-coast-night-image],
 * canvas[data-coast-canvas], optional [data-coast-toggle]/[data-coast-strength].
 * Scene events: coast:time {night:0..1}, coast:pause {paused:boolean,userInitiated?:boolean},
 * coast:strength {strength:0..2}; emits coast:motion {playing,enabled,state}.
 * All source photographs must use centred object-fit: cover in the fallback CSS.
 */
(() => {
  'use strict';

  if (window.__cgauCoastalWaterRuntime) {
    window.__cgauCoastalWaterRuntime.scan(document);
    return;
  }

  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  const number = (value, fallback) => Number.isFinite(Number(value)) && value !== '' && value != null
    ? Number(value) : fallback;

  const vertexSource = `
    attribute vec2 a_position;
    varying vec2 v_uv;
    void main() {
      v_uv = a_position * .5 + .5;
      gl_Position = vec4(a_position, 0., 1.);
    }
  `;

  const fragmentSource = `
// V3 review candidate. Original analytic water shading, not a fluid solver.
// Physical wave/normal/depth approach: NVIDIA GPU Gems, chapter 1, Mark Finch.
precision PRECISION float;
varying vec2 v_uv;
uniform sampler2D u_day;
uniform sampler2D u_night;
uniform vec2 u_cover;
uniform vec2 u_texel;
uniform vec2 u_screen;
uniform vec4 u_light; // photograph-relative x,y, visibility, moon flag
uniform float u_time;
uniform float u_strength;
uniform float u_nightMix;
uniform float u_phase;
uniform float u_warm;
uniform float u_twilight;
uniform vec4 u_weather; // storm, rain, lightning, photo-relative lightning x
uniform float u_horizon;
uniform float u_shore;

const float PI = 3.14159265359;
const float ASPECT = 1672.0 / 941.0;
const float FOCAL = .94;
const float CAMERA_HEIGHT = 1.65;

float sat(float x) { return clamp(x, 0., 1.); }
vec3 linearColour(vec3 colour) { return pow(max(colour, vec3(0.)), vec3(2.2)); }
vec3 displayColour(vec3 colour) { return pow(max(colour, vec3(0.)), vec3(1. / 2.2)); }
float ridge(float x) {
  if(x<.09)return .499;
  if(x<.12)return mix(.499,.475,(x-.09)/.03);
  if(x<.17)return mix(.475,.472,(x-.12)/.05);
  if(x<.23)return mix(.472,.466,(x-.17)/.06);
  if(x<.28)return mix(.466,.454,(x-.23)/.05);
  if(x<.32)return .454;
  if(x<.38)return mix(.454,.434,(x-.32)/.06);
  if(x<.45)return mix(.434,.414,(x-.38)/.07);
  if(x<.49)return mix(.414,.410,(x-.45)/.04);
  if(x<.55)return mix(.410,.433,(x-.49)/.06);
  if(x<.63)return mix(.433,.454,(x-.55)/.08);
  if(x<.70)return mix(.454,.443,(x-.63)/.07);
  if(x<.76)return mix(.443,.468,(x-.70)/.06);
  if(x<.87)return mix(.468,.469,(x-.76)/.11);
  if(x<.95)return mix(.469,.495,(x-.87)/.08);
  return mix(.495,.500,(x-.95)/.05);
}
vec3 photo(vec2 uv) {
  vec3 base=mix(texture2D(u_day, uv).rgb, texture2D(u_night, uv).rgb, u_nightMix);
  float down=1.-uv.y;
  if((u_warm>0. || u_twilight>0.) && down<.505) {
    // Carry atmospheric colour through the silhouette edge: stopping above
    // the ridge leaves an ungraded cyan outline around the island.
    float sky=1.-smoothstep(ridge(uv.x)-.003,ridge(uv.x)+.012,down);
    float low=smoothstep(.10,.48,down);
    // Keep the photograph's cloud luminance and detail, but let a low sun
    // colour those clouds. Gold gathers around the sun; coral spreads away.
    float lightSide=exp(-pow((uv.x-u_light.x)/.40,2.));
    float luminance=dot(base,vec3(.2126,.7152,.0722));
    vec3 coral=mix(vec3(1.,.38,.24),vec3(1.,.29,.34),smoothstep(.12,.48,u_phase));
    vec3 warmHue=mix(coral,vec3(1.,.67,.20),lightSide*.78);
    vec3 warm=warmHue*(luminance*.94+.28)+base*.14;
    base=mix(base,warm,u_warm*sky*(.32+.55*low));
    // Violet is strongest below the upper blue sky. This remains a sky mask,
    // so the island and dry beach never receive a whole-scene magenta filter.
    vec3 violetHue=mix(vec3(.40,.24,.76),vec3(.70,.32,.80),low);
    vec3 violet=violetHue*(luminance*.95+.18)+base*.20;
    base=mix(base,violet,u_twilight*sky*(.55+.30*low));
  }
  // The original dry beach shares the footer's golden-sand grade.
  float sand=smoothstep(u_shore+.045,u_shore+.10,down);
  vec3 gold=base*vec3(1.04,1.20,1.06)+vec3(.08,.065,.0);
  vec3 graded=mix(base,gold,sand*.9*(1.-u_nightMix*.75));
  // Lightning changes surface exposure and cool fill light. The same colour
  // transform is shared with the footer sand, preserving that continuous edge.
  graded=graded*(1.+u_weather.z*1.8)+vec3(.11,.14,.20)*u_weather.z;
  return clamp(graded,0.,1.);
}
float hash(vec2 p) {
  vec3 q = fract(vec3(p.xyx) * .1031);
  q += dot(q, q.yzx + 33.33);
  return fract((q.x + q.y) * q.z);
}
float noise(vec2 p) {
  vec2 cell = floor(p), f = fract(p);
  f = f * f * (3. - 2. * f);
  return mix(mix(hash(cell), hash(cell + vec2(1., 0.)), f.x),
    mix(hash(cell + vec2(0., 1.)), hash(cell + vec2(1., 1.)), f.x), f.y);
}
vec2 noiseGradient(vec2 p) {
  vec2 cell=floor(p), f=fract(p), u=f*f*(3.-2.*f), du=6.*f*(1.-f);
  float a=hash(cell),b=hash(cell+vec2(1.,0.)),c=hash(cell+vec2(0.,1.)),d=hash(cell+1.);
  return vec2(b-a+(a-b-c+d)*u.y,c-a+(a-b-c+d)*u.x)*du;
}

// Return height and exact x/z partial derivatives together. The second harmonic
// sharpens the crest while preserving a longer, rounded trough.
vec3 wave(vec2 p, vec2 direction, float k, float amplitude, float omega, float phase, float footprint) {
  float filter = 1. - smoothstep(.5, 2.3, footprint * k);
  float theta = dot(p, direction) * k + u_time * omega + phase;
  float height = sin(theta) + .18 * sin(2. * theta);
  float gradient = k * (cos(theta) + .36 * cos(2. * theta));
  return vec3(height, gradient * direction) * amplitude * filter;
}

float shoreDistance() { return CAMERA_HEIGHT * FOCAL / (u_shore - u_horizon); }

vec3 swell(vec2 p, float footprint) {
  float z0 = shoreDistance();
  float depth = max(0., (p.y - z0) * .17);
  float damping = smoothstep(.005, .40, depth);
  float dampingU=clamp((depth-.005)/.395,0.,1.);
  float dampingSlope=6.*dampingU*(1.-dampingU)*.17/.395;
  float scale = clamp(u_strength, 0., 1.8);
  vec3 surface = wave(p, normalize(vec2(.025, 1.)), 1.05, .045, .91, 1.7 - z0 * 1.05, footprint);
  surface += wave(p, normalize(vec2(-.40, 1.)), 2.61, .016, 2.24, 2.43, footprint);
  surface += wave(p, normalize(vec2(.79, 1.)), 4.58, .010, 3.10, .87, footprint);
  surface += wave(p, normalize(vec2(1., .38)), 3.77, .009, 2.48, 1.31, footprint);
  // The long wave shoals into a shallow beach instead of moving dry sand.
  return vec3(surface.x*damping,surface.y*damping,surface.z*damping+surface.x*dampingSlope)*scale;
}

vec2 rippleSlope(vec2 p, float footprint) {
  vec3 ripple = wave(p, normalize(vec2(-.30, 1.)), 8.6, .0035, 4.1, .7, footprint);
  ripple += wave(p, normalize(vec2(.46, 1.)), 13.7, .0025, 5.4, 2.1, footprint);
  ripple += wave(p, normalize(vec2(-.82, .57)), 21.1, .0014, 6.9, 4.2, footprint);
  ripple += wave(p, normalize(vec2(.19, 1.)), 30.6, .00085, 8.2, 1.3, footprint);
  ripple += wave(p, normalize(vec2(.91, .41)), 46.8, .00048, 10.6, 5.8, footprint);
  ripple += wave(p, normalize(vec2(-.44, .90)), 65.1, .00030, 12.1, 3.4, footprint);
  vec2 irregular = noiseGradient(p*2.4+vec2(.32,-.18)*u_time)*.035*(1.-smoothstep(.2,1.,footprint*2.4));
  irregular += noiseGradient(p*5.8+vec2(-.61,-.43)*u_time)*.033*(1.-smoothstep(.2,1.,footprint*5.8));
  irregular += noiseGradient(p*13.2+vec2(.7,-1.1)*u_time)*.025*(1.-smoothstep(.2,1.,footprint*13.2));
  return (ripple.yz + irregular) * clamp(u_strength, 0., 1.8);
}

float worldPixel(float z) {
  // A pixel covers progressively more sea in the distance. Removing frequencies
  // above that footprint prevents distant waves becoming sparkling stripes.
  float horizontal = z * ASPECT * u_cover.x / (FOCAL * u_screen.x);
  float vertical = z * z * u_cover.y / (CAMERA_HEIGHT * FOCAL * u_screen.y);
  return max(horizontal, vertical);
}

vec3 environment(vec3 reflected) {
  vec3 direction = normalize(reflected);
  // A rough water surface integrates a broad patch of the environment. Sampling
  // a sharp photographic ridge here creates mirror bands rather than wind chop.
  vec3 left = linearColour(photo(vec2(.10, .72)));
  vec3 right = linearColour(photo(vec2(.90, .72)));
  vec3 zenith = linearColour(photo(vec2(.55, .96)));
  vec3 horizon = mix(left, right, sat(.5 + direction.x * .62));
  return mix(horizon, zenith, smoothstep(.04, .85, max(0., direction.y)));
}

float smith(float n, float k) { return n / (n * (1. - k) + k); }
float sunSpecular(vec3 normal, vec3 view, vec3 light, float footprint) {
  vec3 halfVector = normalize(view + light);
  float nv = max(.025, dot(normal, view));
  float nl = max(0., dot(normal, light));
  float nh = max(0., dot(normal, halfVector));
  float vh = max(0., dot(view, halfVector));
  // Wave normals carry the large scale roughness; this small microfacet lobe
  // supplies highlights between them, broadened as fine waves are filtered out.
  float roughness = .15 + smoothstep(.01, .20, footprint) * .10;
  float a2 = roughness * roughness;
  float denominator = nh * nh * (a2 - 1.) + 1.;
  float distribution = a2 / max(.00002, PI * denominator * denominator);
  float fresnel = .0204 + .9796 * pow(1. - vh, 5.);
  float visibility = smith(nv, roughness * .5) * smith(nl, roughness * .5);
  float energy=distribution * fresnel * visibility / (4. * nv);
  return energy/(1.+energy*6.);
}

void main() {
  vec2 uv = (v_uv - .5) * u_cover + .5;
  float down = 1. - uv.y;
  vec3 original = photo(uv);
  if (down <= u_horizon + .001 || down >= u_shore + .070) {
    gl_FragColor = vec4(original, 1.);
    return;
  }

  vec3 ray = normalize(vec3((uv.x - .5) * ASPECT, u_horizon - down, FOCAL));
  vec3 origin = vec3(0., CAMERA_HEIGHT, 0.);
  float flatDistance = CAMERA_HEIGHT / max(.001, -ray.y);
  float distance = flatDistance;
  vec3 point = origin + ray * distance;
  float footprint = worldPixel(point.z);

  // Intersect the camera ray with geometric undulations, rather than distorting
  // a flat photograph. Capped Newton steps remain stable at grazing angles.
  for (int iteration = 0; iteration < 3; iteration++) {
    point = origin + ray * distance;
    vec3 field = swell(point.xz, footprint);
    float denominator = min(-.025, ray.y - dot(field.yz, ray.xz));
    float step = (point.y - field.x) / denominator;
    distance -= clamp(step, -.10 * flatDistance, .10 * flatDistance);
    distance = clamp(distance, .70 * flatDistance, 1.30 * flatDistance);
  }
  point = origin + ray * distance;
  footprint = worldPixel(point.z);
  vec3 field = swell(point.xz, footprint);
  vec2 slope = field.yz + rippleSlope(point.xz, footprint);
  vec3 normal = normalize(vec3(-slope.x, 1., -slope.y));
  vec3 view = -ray;

  float z0 = shoreDistance();
  float depth = max(0., (point.z - z0) * .17 + field.x);
  float shallow = 1. - smoothstep(.06, 1.8, depth);
  float night = u_nightMix;
  vec3 light = normalize(vec3((u_light.x - .5) * ASPECT, u_horizon - u_light.y, FOCAL));
  float illumination = .84 + .16 * max(0., dot(normal, light));

  // The seabed is a two-dimensional sand field in world metres. Sampling a
  // narrow strip of photographic sand here made a column for every source
  // pixel; no photograph lookup contributes to this underwater material.
  vec2 bedPoint=point.xz+normal.xz*min(.16,depth*.12);
  float fineFilter=1.-smoothstep(.02,.065,footprint);
  float fineSand=noise(bedPoint*38.7)*.55+noise(bedPoint*71.3+12.5)*.45;
  float broadSand=noise(bedPoint*2.7)*.65+noise(bedPoint*7.9+4.1)*.35;
  float grain=.96+.10*(broadSand-.5)+fineFilter*.11*(fineSand-.5);
  vec3 sand=linearColour(mix(vec3(.69,.61,.40),vec3(.076,.096,.120),night))*grain;
  // Suspended grains and absorption limit the clear, shallow band to the last
  // few metres. Farther water retains a turquoise body underneath reflection.
  vec3 attenuation=exp(-vec3(2.7,1.85,1.50)*depth)*(1.-smoothstep(.35,.90,depth));
  vec3 bodyColour=mix(vec3(.020,.170,.205),vec3(.0006,.0035,.0075),night);
  vec3 transmitted=sand*attenuation+bodyColour*(1.-attenuation);
  transmitted*=illumination;

  vec3 reflected = environment(reflect(ray, normal));
  float cosine = max(0., dot(normal, view));
  float roughCosine = sqrt(cosine*cosine+.17*.17);
  float fresnel = .0204 + .9796 * pow(1. - min(1.,roughCosine), 5.);
  // Wind-driven surface roughness broadens reflected sky into the teal body.
  fresnel=min(fresnel,.48);
  vec3 water = mix(transmitted, reflected, fresnel);
  float specular = sunSpecular(normal, view, light, footprint) * u_light.z;
  vec3 lightColour = mix(vec3(1.0, .80, .53), vec3(.49, .65, 1.0), u_light.w);
  float lightPower = mix(.35, .10, u_light.w);
  water += lightColour * specular * lightPower;
  water += bodyColour * max(0., field.x) * 1.2 * (1. - fresnel);

  // The photograph resolves far-off wavelets that fall below our geometric
  // pixel footprint. Retain only their fine luminance at exact image positions;
  // their original colour and broad lighting never replace the water material.
  float distanceDetail=1.-smoothstep(u_horizon+.036,u_horizon+.104,down);
  if(distanceDetail>.001) {
    vec3 lumaWeights=vec3(.2126,.7152,.0722);
    float centreLuma=dot(original,lumaWeights);
    float broadLuma=dot((photo(uv+vec2(-.006,.006))
      +photo(uv+vec2(.006,-.006)))*.5,lumaWeights);
    float detail=clamp((centreLuma+.025)/(broadLuma+.025),.86,1.14);
    water*=mix(1.,detail,distanceDetail*.78);
  }


  // The same long swell that approaches the shore drives the run-up/backwash.
  // Its waterline moves in world metres, so the perspective sets its image size.
  float shorePhase = u_time * .91 + 1.7;
  float alongShore = .14 * sin(point.x * .69+.4) + .065 * sin(point.x * 1.6+2.3) + .025*sin(point.x*4.8);
  float frontZ = z0 + .12 - .46 * sin(shorePhase - .68) + alongShore;
  float toFront = point.z - frontZ;
  float edgeWidth = max(.024, footprint * 1.4);
  float coverage = smoothstep(-edgeWidth, edgeWidth, toFront);
  float advancing = .5 + .5 * cos(shorePhase - .68);

  vec2 foamCoordinate = point.xz * vec2(7.6, 10.2) + vec2(.07, -.28) * u_time;
  float holes = noise(foamCoordinate) * .72 + noise(foamCoordinate * 2.8 + 9.2) * .28;
  float lace = smoothstep(.33, .65, holes);
  float leadingFoam = exp(-pow((toFront - .045) / .115, 2.)) * (.42 + advancing * .42);
  float crest = sin((point.z - z0) * 1.05 + shorePhase + point.x * .026);
  float breaker = smoothstep(.76, .99, crest)
    * exp(-pow((point.z - z0 - .72) / 1.12, 2.));
  float trails = exp(-max(0., toFront) * 3.) * pow(max(0., holes - .28), 3.) * .9;
  float foam = clamp((leadingFoam + breaker * .66) * lace + trails, 0., .92);
  vec3 foamColour = mix(vec3(.76, .79, .75), vec3(.028, .047, .078), night);
  water = mix(water, foamColour * (.84 + .16 * illumination), foam * coverage);

  // A passing cloud suppresses the bright sky reflection; its lightning is
  // broken by the existing wave normals, keeping light on the moving surface.
  water *= 1. - .24 * u_weather.x;
  float flashPath = exp(-pow((uv.x-u_weather.w)/( .035+.17*clamp((down-u_horizon)/(u_shore-u_horizon),0.,1.)),2.));
  float flashRipple = .28 + .72*pow(clamp(.5+.5*sin(point.z*4.3+point.x*.6+normal.x*28.),0.,1.),2.);
  water = water*(1.+u_weather.z*1.1)+vec3(.012,.022,.040)*u_weather.z;
  water += vec3(.085,.120,.185) * u_weather.z * flashPath * flashRipple;

  // When a wave recedes it uncovers wet sand, not the old photographed foam.
  // The replacement fades out before reaching the dry foreground beach.
  float beachBand = smoothstep(u_shore - .050, u_shore - .028, down)
    * (1. - smoothstep(u_shore + .034, u_shore + .069, down));
  vec2 wetUv = vec2(uv.x, uv.y - .062);
  vec3 wetSand = linearColour(photo(wetUv)) * mix(.78, .93, night);
  float wetFresnel = .014 + .18 * pow(1. - cosine, 5.);
  wetSand = mix(wetSand, reflected, wetFresnel);
  vec3 beach = mix(linearColour(original), wetSand, beachBand);

  // A narrow distance fade joins the real photographic horizon seamlessly.
  float horizonBlend = smoothstep(u_horizon + .003, u_horizon + .025, down);
  vec3 result = mix(beach, water, coverage * horizonBlend);
  // This renderer intentionally uses no post-process glitter, vignette, bloom
  // or camera movement. The photograph supplies the scene's overall exposure.
  gl_FragColor = vec4(clamp(displayColour(result), 0., 1.), 1.);
}
  `;

  class CoastalScene {
    constructor(scene, previousState = null) {
      this.scene = scene;
      this.ambientParent = scene.closest('[data-coastal-section]');
      this.dayImage = scene.querySelector('[data-coast-image]');
      this.nightImage = scene.querySelector('[data-coast-night-image]');
      this.canvas = scene.querySelector('[data-coast-canvas]');
      this.toggle = scene.querySelector('[data-coast-toggle]');
      this.strengthInput = scene.querySelector('[data-coast-strength]');
      this.cleanups = [];
      this.frame = 0;
      this.frames = 0;
      this.time = previousState?.time ?? 0;
      this.previousStamp = 0;
      this.lastDrawStamp = 0;
      this.visible = false;
      this.suspended = false;
      this.destroyed = false;
      this.contextLost = false;
      this.userPaused = previousState?.userPaused ?? false;
      this.ambientPaused = previousState?.ambientPaused ?? (this.ambientParent?.dataset.cgauMotionPaused === 'true');
      this.motionOverride = previousState?.motionOverride ?? false;
      this.ready = false;
      this.needsPaint = true;
      this.night = clamp(number(scene.dataset.night, 0), 0, 1);
      this.phase = clamp(number(scene.dataset.phase, .25), 0, 1);
      this.warm = clamp(number(scene.dataset.warm, 0), 0, 1);
      this.twilight = clamp(number(scene.dataset.twilight, 0), 0, 1);
      this.weather = {storm:0,rain:0,flash:0};
      this.strength = clamp(number(this.strengthInput?.value ?? scene.dataset.coastStrength, 1), 0, 2);
      this.horizon = clamp(number(scene.dataset.coastHorizon, .508), 0, .98);
      this.shore = clamp(number(scene.dataset.coastShore, .812), this.horizon + .01, 1.1);
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.tick = this.tick.bind(this);
      scene.dataset.renderer = 'fallback';
      scene.dataset.frames = '0';
      scene.dataset.time = '0';
      if (!this.dayImage || !this.canvas) return;
      this.canvas.style.visibility = 'hidden';

      this.listen(this.canvas, 'webglcontextlost', event => {
        event.preventDefault();
        this.contextLost = true;
        this.ready = false;
        this.dayTexture = this.nightTexture = this.buffer = this.program = null;
        this.showFallback('context-lost');
      });
      this.listen(this.canvas, 'webglcontextrestored', () => {
        this.contextLost = false;
        this.initialise();
      });
      this.listen(document, 'visibilitychange', () => this.updateMotion());
      this.listen(this.motionPreference, 'change', () => {
        this.motionOverride = false;
        this.updateMotion();
      });
      this.listen(scene, 'coast:time', event => {
        this.night = clamp(number(event.detail?.night, this.night), 0, 1);
        this.phase = clamp(number(event.detail?.phase, this.phase), 0, 1);
        this.warm = clamp(number(event.detail?.warm, this.warm), 0, 1);
        this.twilight = clamp(number(event.detail?.twilight, this.twilight), 0, 1);
        scene.dataset.night = this.night.toFixed(3);
        this.drawForInput();
      });
      this.listen(scene, 'coast:weather', event => {
        for (const key of ['storm','rain','flash']) this.weather[key]=clamp(number(event.detail?.[key],0),0,1);
        this.updateFallbackLight();
        this.drawForInput();
      });
      const pauseFromEvent = event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.userPaused = event.detail.paused;
        if (this.userPaused) this.motionOverride = false;
        else if (event.detail.userInitiated === true) this.motionOverride = true;
        this.updateMotion();
      };
      const ambientFromEvent = event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.ambientPaused = event.detail.paused;
        this.updateMotion();
      };
      this.listen(scene, 'coast:pause', pauseFromEvent);
      this.listen(scene, 'cgau:ambient-motion-change', ambientFromEvent);
      this.listen(window, 'cgau:ambient-motion-change', event => {
        if (event.target === window) ambientFromEvent(event);
      });
      const ambientParent = this.ambientParent;
      if (ambientParent && ambientParent !== scene) {
        this.listen(ambientParent, 'cgau:ambient-motion-change', ambientFromEvent);
      }
      this.listen(scene, 'coast:strength', event => {
        this.strength = clamp(number(event.detail?.strength, this.strength), 0, 2);
        this.drawForInput();
      });
      if (this.toggle) this.listen(this.toggle, 'click', () => {
        if (this.wantsMotion()) this.userPaused = true;
        else {
          this.userPaused = false;
          this.motionOverride = true;
        }
        this.updateMotion();
      });
      if (this.strengthInput) this.listen(this.strengthInput, 'input', () => {
        this.strength = clamp(number(this.strengthInput.value, 1), 0, 2);
        this.drawForInput();
      });
      this.listen(this.dayImage, 'load', () => this.initialise());
      this.listen(this.dayImage, 'error', () => this.showFallback('image-error'));
      if (this.nightImage) {
        this.listen(this.nightImage, 'load', () => {
          if (this.ready) {
            try {
              this.uploadTexture(this.nightTexture, this.nightImage);
              this.nightReady = true;
              this.drawForInput();
            } catch (error) { this.showFallback('night-texture-error', error); }
          }
        });
      }

      const bounds = scene.getBoundingClientRect();
      this.visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.updateMotion();
        }, { rootMargin: '80px', threshold: 0 });
        this.intersectionObserver.observe(scene);
      } else this.visible = true;
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(scene);
      } else this.listen(window, 'resize', () => this.resize());
      if (this.dayImage.complete && this.dayImage.naturalWidth) this.initialise();
      this.updateMotion();
    }

    listen(target, type, listener) {
      target.addEventListener(type, listener);
      this.cleanups.push(() => target.removeEventListener(type, listener));
    }

    compile(type, source) {
      const shader = this.gl.createShader(type);
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);
      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        const error = this.gl.getShaderInfoLog(shader);
        this.gl.deleteShader(shader);
        throw new Error(`Coast shader: ${error}`);
      }
      return shader;
    }

    initialise() {
      if (this.destroyed || this.contextLost || !this.dayImage.naturalWidth) return;
      this.stop();
      this.ready = false;
      try {
        this.gl = this.canvas.getContext('webgl', {
          alpha: false, antialias: false, depth: false, stencil: false,
          premultipliedAlpha: false, preserveDrawingBuffer: false,
          powerPreference: 'low-power'
        });
        if (!this.gl) { this.showFallback('webgl-unavailable'); return; }
        this.releaseResources();
        const gl = this.gl;
        const high = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
        const precision = high?.precision > 0 ? 'highp' : 'mediump';
        const vertex = this.compile(gl.VERTEX_SHADER, vertexSource);
        const fragment = this.compile(gl.FRAGMENT_SHADER, fragmentSource.replace('PRECISION', precision));
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertex);
        gl.attachShader(this.program, fragment);
        gl.linkProgram(this.program);
        gl.deleteShader(vertex);
        gl.deleteShader(fragment);
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
          throw new Error(`Coast program: ${gl.getProgramInfoLog(this.program)}`);
        }
        gl.useProgram(this.program);
        this.buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
        const position = gl.getAttribLocation(this.program, 'a_position');
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
        this.uniforms = {};
        ['day', 'night', 'cover', 'texel', 'screen', 'light', 'time', 'strength', 'nightMix', 'phase', 'warm', 'twilight', 'weather', 'horizon', 'shore'].forEach(name => {
          this.uniforms[name] = gl.getUniformLocation(this.program, `u_${name}`);
        });
        this.dayTexture = gl.createTexture();
        this.nightTexture = gl.createTexture();
        this.uploadTexture(this.dayTexture, this.dayImage);
        this.nightReady = Boolean(this.nightImage?.complete && this.nightImage.naturalWidth);
        this.uploadTexture(this.nightTexture, this.nightReady ? this.nightImage : this.dayImage);
        gl.uniform1i(this.uniforms.day, 0);
        gl.uniform1i(this.uniforms.night, 1);
        this.ready = true;
        this.resize();
        this.drawStill();
        this.scene.dataset.renderer = 'webgl';
        delete this.scene.dataset.rendererReason;
        this.updateMotion();
      } catch (error) { this.showFallback('renderer-error', error); }
    }

    uploadTexture(texture, image) {
      const gl = this.gl;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      const error = gl.getError();
      if (error !== gl.NO_ERROR) throw new Error(`Coast texture upload failed (${error}).`);
    }

    resize() {
      if (!this.ready || this.destroyed || this.contextLost) return;
      const { width, height } = this.canvas.getBoundingClientRect();
      if (width < 1 || height < 1) return;
      // Keep large desktop canvases below 1.8M pixels; don't burn phone battery
      // rendering scenery at a three-times device pixel ratio.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.65, Math.sqrt(1800000 / (width * height)));
      const maxSize = this.gl.getParameter(this.gl.MAX_RENDERBUFFER_SIZE);
      const targetWidth = Math.max(1, Math.min(maxSize, Math.round(width * dpr)));
      const targetHeight = Math.max(1, Math.min(maxSize, Math.round(height * dpr)));
      if (this.canvas.width !== targetWidth) this.canvas.width = targetWidth;
      if (this.canvas.height !== targetHeight) this.canvas.height = targetHeight;
      const viewAspect = width / height;
      this.viewWidth = width;
      const imageAspect = this.dayImage.naturalWidth / this.dayImage.naturalHeight;
      this.cover = viewAspect > imageAspect ? [1, imageAspect / viewAspect] : [viewAspect / imageAspect, 1];
      this.gl.viewport(0, 0, targetWidth, targetHeight);
      this.drawStill();
    }

    drawForInput() {
      // Lighting tweens may dispatch at 60/120Hz. Their latest values are picked
      // up by the next 30Hz water frame; paused controls still update immediately.
      // Hidden/inset-offscreen scenes only retain their latest pending values.
      this.needsPaint = true;
      if (this.scene.dataset.motion !== 'running') this.drawStill();
    }

    drawStill() {
      if (!this.ready || this.destroyed || this.contextLost || !this.cover || this.suspended || document.hidden || !this.visible || this.ambientPaused) {
        this.needsPaint = true;
        return;
      }
      try {
        const gl = this.gl;
        gl.useProgram(this.program);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.dayTexture);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.nightTexture);
        gl.uniform2f(this.uniforms.cover, this.cover[0], this.cover[1]);
        gl.uniform2f(this.uniforms.texel, 1 / this.dayImage.naturalWidth, 1 / this.dayImage.naturalHeight);
        gl.uniform2f(this.uniforms.screen, this.canvas.width, this.canvas.height);
        // Use the sky renderer's photographed-coordinate light position when
        // current; derive the same arc if its next frame has not painted yet.
        const data = this.scene.dataset;
        const moon = this.phase >= .5;
        const progress = clamp(moon ? (this.phase - .5) * 2 : this.phase * 2, 0, 1);
        const skyCurrent = Math.abs(number(data.skyPhase, -1) - this.phase) < .004;
        const key = moon ? 'moon' : 'sun';
        const lightX = skyCurrent ? number(data[key + 'X'], .5) : .5 + (progress - .5) * this.cover[0] * .88;
        const arcPeak = number(data.skyArcPeak, .20);
        const lightY = skyCurrent ? number(data[key + 'Y'], .20) : .53 - (.53 - arcPeak) * Math.sin(Math.PI * progress);
        const visibility = skyCurrent ? number(data.skyBodyVisible, 0) : clamp((this.horizon - lightY) / .035, 0, 1);
        gl.uniform4f(this.uniforms.light, lightX, lightY, visibility, moon ? 1 : 0);
        gl.uniform1f(this.uniforms.time, this.time);
        gl.uniform1f(this.uniforms.strength, this.strength);
        gl.uniform1f(this.uniforms.nightMix, this.nightReady ? this.night : 0);
        gl.uniform1f(this.uniforms.phase, this.phase);
        gl.uniform1f(this.uniforms.warm, this.warm);
        gl.uniform1f(this.uniforms.twilight, this.twilight);
        const weatherScreenX = number(this.ambientParent?.dataset.weatherTargetX, this.viewWidth * .2) / this.viewWidth;
        const weatherSourceX = .5 + (weatherScreenX - .5) * this.cover[0];
        gl.uniform4f(this.uniforms.weather, this.weather.storm, this.weather.rain, this.weather.flash, weatherSourceX);
        gl.uniform1f(this.uniforms.horizon, this.horizon);
        gl.uniform1f(this.uniforms.shore, this.shore);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        this.needsPaint = false;
        this.canvas.style.visibility = 'visible';
        this.frames++;
        this.scene.dataset.frames = String(this.frames);
        this.scene.dataset.time = this.time.toFixed(3);
      } catch (error) { this.showFallback('draw-error', error); }
    }

    wantsMotion() {
      return !this.userPaused && (!this.motionPreference.matches || this.motionOverride);
    }

    updateFallbackLight() {
      if (this.destroyed || this.suspended || document.hidden || !this.visible || this.ambientPaused) return;
      const value=this.weather.flash.toFixed(3);
      if (value===this.fallbackFlash) return;
      this.fallbackFlash=value;
      this.scene.style.setProperty('--coast-fallback-flash',value);
    }

    updateMotion() {
      if (this.destroyed) return;
      this.updateFallbackLight();
      const enabled = this.wantsMotion();
      if (this.toggle) {
        this.toggle.textContent = enabled ? 'Pause scenery' : 'Play scenery';
        this.toggle.setAttribute('aria-label', enabled ? 'Pause scenery' : 'Play scenery');
        this.toggle.setAttribute('aria-pressed', String(!enabled));
      }
      let state = 'running';
      if (!this.ready || this.contextLost) state = 'fallback';
      else if (this.suspended || document.hidden) state = 'hidden';
      else if (this.userPaused) state = 'paused';
      else if (this.ambientPaused) state = 'ambient-paused';
      else if (!enabled) state = 'reduced';
      else if (!this.visible) state = 'offscreen';
      const changed = this.scene.dataset.motion !== state;
      this.scene.dataset.motion = state;
      if (state === 'running') {
        if (!this.frame) {
          this.previousStamp = 0;
          this.lastDrawStamp = 0;
          this.frame = requestAnimationFrame(this.tick);
        }
      } else {
        this.stop();
        // A paused/reduced scene has no RAF to catch up after an offscreen,
        // hidden or ambient pause. Render its latest still once it is drawable.
        if (this.needsPaint) this.drawStill();
      }
      if (changed) this.scene.dispatchEvent(new CustomEvent('coast:motion', {
        detail: { playing: state === 'running', enabled, state }
      }));
    }

    tick(stamp) {
      this.frame = 0;
      if (!this.ready || this.destroyed || this.suspended || this.contextLost || this.ambientPaused || document.hidden || !this.visible || !this.wantsMotion()) {
        this.updateMotion();
        return;
      }
      if (this.previousStamp) this.time += Math.min(.1, (stamp - this.previousStamp) / 1000);
      this.previousStamp = stamp;
      if (stamp - this.lastDrawStamp >= 1000 / 30 - .5) {
        this.lastDrawStamp = stamp;
        this.drawStill();
      }
      if (this.ready) this.frame = requestAnimationFrame(this.tick);
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.previousStamp = 0;
    }

    showFallback(reason, error) {
      this.ready = false;
      this.stop();
      if (this.canvas) this.canvas.style.visibility = 'hidden';
      this.scene.dataset.renderer = 'fallback';
      this.scene.dataset.rendererReason = reason;
      this.updateMotion();
      if (error) console.warn('[Coastal scenery] The still photograph remains available.', error);
    }

    releaseResources() {
      if (!this.gl) return;
      if (this.dayTexture) this.gl.deleteTexture(this.dayTexture);
      if (this.nightTexture) this.gl.deleteTexture(this.nightTexture);
      if (this.buffer) this.gl.deleteBuffer(this.buffer);
      if (this.program) this.gl.deleteProgram(this.program);
      this.dayTexture = this.nightTexture = this.buffer = this.program = null;
    }

    suspend() {
      this.suspended = true;
      this.updateMotion();
    }

    resume() {
      if (this.destroyed) return;
      this.suspended = false;
      this.resize();
      this.updateMotion();
    }

    destroy() {
      if (this.destroyed) return;
      this.stop();
      this.destroyed = true;
      this.ready = false;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.cleanups.forEach(cleanup => cleanup());
      this.cleanups.length = 0;
      this.releaseResources();
      if (this.canvas) this.canvas.style.visibility = 'hidden';
      this.scene.dataset.renderer = 'fallback';
      this.scene.dataset.motion = 'destroyed';
      this.scene.dispatchEvent(new CustomEvent('coast:motion', {
        detail: { playing: false, enabled: false, state: 'destroyed' }
      }));
    }
  }

  const registry = new Map();
  const dispose = scene => {
    const instance = registry.get(scene);
    registry.delete(scene);
    instance?.destroy();
  };
  const scan = (root = document) => {
    // Some theme-editor replacements omit unload. Sweep the old disconnected
    // nodes before mounting their replacements so no orphan keeps rendering.
    for (const scene of registry.keys()) {
      if (scene.isConnected === false) dispose(scene);
    }
    const scenes = [...(root.querySelectorAll?.('.coast-scene') ?? [])];
    if (root.matches?.('.coast-scene')) scenes.unshift(root);
    for (const scene of scenes) {
      if (scene.isConnected === false) continue;
      const previous = registry.get(scene);
      const dayImage = scene.querySelector('[data-coast-image]');
      const nightImage = scene.querySelector('[data-coast-night-image]');
      const canvas = scene.querySelector('[data-coast-canvas]');
      if (registry.has(scene) && !previous) continue; // A synchronous mount is already in progress.
      if (previous && previous.canvas === canvas && previous.dayImage === dayImage && previous.nightImage === nightImage) continue;
      const state = previous ? {
        userPaused: previous.userPaused, ambientPaused: previous.ambientPaused,
        motionOverride: previous.motionOverride, time: previous.time
      } : null;
      if (previous) dispose(scene);
      if (!dayImage || !canvas) continue;
      registry.set(scene, null);
      try { registry.set(scene, new CoastalScene(scene, state)); }
      catch (error) {
        registry.delete(scene);
        console.warn('[Coastal scenery] Unable to mount this scene; the still photograph remains available.', error);
      }
    }
  };
  const destroyAll = () => [...registry.keys()].forEach(dispose);
  window.__cgauCoastalWaterRuntime = {
    version: '2026-09-07.v3-water-5', scan, destroyAll,
    get size() { return registry.size; }
  };

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    scan();
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event => {
      for (const scene of registry.keys()) {
        if (event.target === scene || event.target.contains?.(scene)) dispose(scene);
      }
    });
    window.addEventListener('pagehide', event => {
      if (event.persisted) registry.forEach(instance => instance?.suspend());
      else destroyAll();
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        scan();
        registry.forEach(instance => instance?.resume());
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
